// Список користувачів + сегментація (активні / сплячі / заблокували / Premium)
// + фільтр по періоду реєстрації (сьогодні / тиждень / місяць / весь час).

import { useState } from 'react';
import {
  api,
  type AdminUser,
  type UserStatusFilter,
  type UserPeriodFilter,
} from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

export function AdminUsers() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<UserStatusFilter>('all');
  const [period, setPeriod] = useState<UserPeriodFilter>('all');

  const stats = useFetch(() => api.admin.userStats(), []);
  const list = useFetch(
    () => api.admin.listUsers({
      search: debounced || undefined,
      status,
      period,
      limit: 100,
    }),
    [debounced, status, period],
  );
  const [error, setError] = useState<string | null>(null);
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState<{
    checked: number;
    newlyBlocked: number;
    durationMs: number;
  } | null>(null);

  const onSearch = (val: string) => {
    setSearch(val);
    setTimeout(() => setDebounced(val), 300);
  };

  async function probeBlocked() {
    if (probing) return;
    if (
      !confirm(
        'Просканувати всіх юзерів на блокування?\n\n' +
          'Бот тихо "пінгне" кожного через sendChatAction (юзер нічого не побачить). ' +
          'Зайнявши 1-3 хв в залежності від кількості. Заблоковані з\'являться у вкладці "Вийшли".',
      )
    )
      return;
    setProbing(true);
    setProbeResult(null);
    setError(null);
    try {
      const res = await api.admin.probeBlockedUsers();
      setProbeResult(res);
      reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка сканування');
    } finally {
      setProbing(false);
    }
  }

  const reloadAll = () => {
    list.reload();
    stats.reload();
  };

  async function toggleRole(u: AdminUser) {
    setError(null);
    try {
      const newRole = u.role === 'ADMIN' ? 'USER' : 'ADMIN';
      await api.admin.updateUser(u.id, { role: newRole });
      reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка');
    }
  }

  async function setPremiumMonths(u: AdminUser, months: number) {
    setError(null);
    const name = u.firstName ?? u.username ?? u.id;
    const ok = months === 0
      ? confirm(`Зняти Premium з ${name}? Юзеру одразу прийде сповіщення.`)
      : confirm(`Видати Premium на ${months} міс. для ${name}?`);
    if (!ok) return;
    try {
      await api.admin.setUserPremium(u.id, { months });
      reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка');
    }
  }

  async function setPremiumTrial(u: AdminUser, days: number) {
    setError(null);
    const name = u.firstName ?? u.username ?? u.id;
    if (!confirm(`Дати пробний Premium на ${days} ${days === 1 ? 'день' : days < 5 ? 'дні' : 'днів'} для ${name}?`))
      return;
    try {
      await api.admin.setUserPremium(u.id, { days, isTrial: true });
      reloadAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Помилка');
    }
  }

  return (
    <div className="space-y-3 p-4">
      {/* === Загальна статистика === */}
      {stats.data && (
        <section className="grid grid-cols-3 gap-2">
          <StatTile label="Сьогодні" value={stats.data.newRegistrations.today.toString()} sub="нових" />
          <StatTile label="Тиждень" value={stats.data.newRegistrations.week.toString()} sub="нових" />
          <StatTile label="Місяць" value={stats.data.newRegistrations.month.toString()} sub="нових" />
        </section>
      )}

      {/* === Кнопка пробінгу заблокованих === */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={probeBlocked}
          disabled={probing}
          className="rounded-lg bg-tg-secondary-bg px-3 py-2 text-xs font-semibold text-tg-text disabled:opacity-50"
        >
          {probing ? '⏳ Сканую…' : '🔍 Просканувати на блокування'}
        </button>
        {probeResult && (
          <div className="flex-1 rounded-lg bg-tg-success/10 px-3 py-2 text-xs text-tg-success">
            ✓ Перевірено {probeResult.checked} юзерів
            {probeResult.newlyBlocked > 0 && (
              <strong> · знайдено {probeResult.newlyBlocked} нових заблокованих</strong>
            )}
            {probeResult.newlyBlocked === 0 && ' · нових заблокованих не знайдено'}
            <span className="ml-1 text-tg-hint">
              ({Math.round(probeResult.durationMs / 1000)}с)
            </span>
          </div>
        )}
      </div>

      {/* === Фільтр-таби: статус === */}
      {stats.data && (
        <div className="-mx-1 flex gap-1.5 overflow-x-auto pb-1">
          <Tab active={status === 'all'} onClick={() => setStatus('all')} count={stats.data.total}>
            Усі
          </Tab>
          <Tab
            active={status === 'active'}
            onClick={() => setStatus('active')}
            count={stats.data.byStatus.active}
            color="success"
          >
            🟢 Активні
          </Tab>
          <Tab
            active={status === 'inactive'}
            onClick={() => setStatus('inactive')}
            count={stats.data.byStatus.inactive}
            color="muted"
          >
            😴 Сплячі
          </Tab>
          <Tab
            active={status === 'blocked'}
            onClick={() => setStatus('blocked')}
            count={stats.data.byStatus.blocked}
            color="destructive"
          >
            🚫 Вийшли
          </Tab>
          <Tab
            active={status === 'premium'}
            onClick={() => setStatus('premium')}
            count={stats.data.byStatus.premium}
            color="warning"
          >
            👑 Premium
          </Tab>
        </div>
      )}

      {/* === Фільтр-чіпи: період реєстрації === */}
      <div className="-mx-1 flex gap-1.5 overflow-x-auto pb-1">
        <span className="shrink-0 self-center text-[10px] font-semibold uppercase tracking-wide text-tg-hint">
          Зайшли:
        </span>
        <Chip active={period === 'all'} onClick={() => setPeriod('all')}>За весь час</Chip>
        <Chip active={period === 'today'} onClick={() => setPeriod('today')}>Сьогодні</Chip>
        <Chip active={period === 'week'} onClick={() => setPeriod('week')}>Тиждень</Chip>
        <Chip active={period === 'month'} onClick={() => setPeriod('month')}>Місяць</Chip>
      </div>

      <input
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Пошук за ім'ям або username…"
        className="w-full rounded-lg bg-tg-secondary-bg px-3 py-2 text-sm placeholder:text-tg-hint"
      />

      {error && (
        <div className="card border border-tg-destructive text-sm text-tg-destructive">{error}</div>
      )}
      {list.loading && <Loader />}
      {list.error && <ErrorView error={list.error} onRetry={list.reload} />}

      {list.data && (
        <>
          <p className="text-xs text-tg-hint">
            Знайдено: <strong>{list.data.total}</strong>
            {status !== 'all' && ` · ${labelForStatus(status)}`}
            {period !== 'all' && ` · ${labelForPeriod(period)}`}
          </p>

          {list.data.items.length === 0 && (
            <p className="card text-center text-sm text-tg-hint">
              За цим фільтром нікого не знайдено
            </p>
          )}

          <div className="space-y-2">
            {list.data.items.map((u) => (
              <UserCard
                key={u.id}
                u={u}
                onToggleRole={() => toggleRole(u)}
                onPremiumMonths={(m) => setPremiumMonths(u, m)}
                onPremiumTrial={(d) => setPremiumTrial(u, d)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ===== Subcomponents =====

function StatTile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg bg-tg-secondary-bg p-2.5">
      <div className="text-[10px] uppercase tracking-wide text-tg-hint">{label}</div>
      <div className="mt-1 text-xl font-bold leading-none">{value}</div>
      <div className="mt-0.5 text-[10px] text-tg-hint">{sub}</div>
    </div>
  );
}

function Tab({
  active,
  count,
  color,
  onClick,
  children,
}: {
  active: boolean;
  count: number;
  color?: 'success' | 'muted' | 'destructive' | 'warning';
  onClick: () => void;
  children: React.ReactNode;
}) {
  // Колір цифри в дужках
  const countCls = active
    ? 'text-tg-button-text/80'
    : color === 'success'
      ? 'text-tg-success'
      : color === 'destructive'
        ? 'text-tg-destructive'
        : color === 'warning'
          ? 'text-yellow-500'
          : 'text-tg-hint';
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium ${
        active
          ? 'bg-tg-button text-tg-button-text'
          : 'bg-tg-secondary-bg text-tg-text'
      }`}
    >
      {children} <span className={`${countCls} ml-0.5`}>· {count}</span>
    </button>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${
        active
          ? 'bg-tg-button text-tg-button-text'
          : 'bg-tg-bg/40 text-tg-hint'
      }`}
    >
      {children}
    </button>
  );
}

function UserCard({
  u,
  onToggleRole,
  onPremiumMonths,
  onPremiumTrial,
}: {
  u: AdminUser;
  onToggleRole: () => void;
  onPremiumMonths: (m: number) => void;
  onPremiumTrial: (d: number) => void;
}) {
  const isPremium = !!u.premiumUntil && new Date(u.premiumUntil) > new Date();
  const isBlocked = !!u.botBlockedAt;
  const lastSeen = formatRelative(u.lastActiveAt);
  const inactiveDays = Math.floor(
    (Date.now() - new Date(u.lastActiveAt).getTime()) / (1000 * 60 * 60 * 24),
  );
  const isInactive = !isBlocked && inactiveDays >= 30;

  return (
    <div className={`card space-y-2 ${isBlocked ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-3">
        {u.photoUrl ? (
          <img src={u.photoUrl} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <div className="grid h-10 w-10 place-items-center rounded-full bg-tg-bg text-sm">👤</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-medium">
              {u.firstName ?? 'Без імені'}
            </span>
            {u.username && (
              <span className="text-xs text-tg-hint">@{u.username}</span>
            )}
            {u.email && (
              <span className="rounded bg-tg-link/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-link">
                📧 пошта
              </span>
            )}
            {isPremium && (
              <span className="rounded bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-500">
                👑
              </span>
            )}
            {isBlocked && (
              <span className="rounded bg-tg-destructive/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-destructive">
                🚫 Вийшов
              </span>
            )}
            {isInactive && (
              <span className="rounded bg-tg-hint/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-hint">
                😴 {inactiveDays}д
              </span>
            )}
            <SourceBadge source={u.source} />
          </div>
          <div className="mt-0.5 text-xs text-tg-hint">
            Lv {u.level} · {u.xp} XP · 🔥 {u.streakDays}
            {u.refereeCount > 0 && (
              <span className="ml-1 text-tg-success">· 👥 запросив {u.refereeCount}</span>
            )}
            {u.telegramId && ` · tg:${u.telegramId}`}
            {u.email && ` · ${u.email}`}
          </div>
          <div className="mt-0.5 text-[11px] text-tg-hint">
            Зайшов: {new Date(u.createdAt).toLocaleDateString('uk-UA')} · Останній раз: {lastSeen}
            {isPremium && (
              <span className="ml-1 text-tg-success">
                · Premium до {new Date(u.premiumUntil!).toLocaleDateString('uk-UA')}
              </span>
            )}
            {isBlocked && (
              <span className="ml-1 text-tg-destructive">
                · Заблокував {new Date(u.botBlockedAt!).toLocaleDateString('uk-UA')}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onToggleRole}
          className={`shrink-0 rounded-lg px-3 py-1.5 text-xs ${
            u.role === 'ADMIN'
              ? 'bg-tg-button text-tg-button-text'
              : 'bg-tg-secondary-bg text-tg-text'
          }`}
        >
          {u.role}
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        <button
          onClick={() => onPremiumTrial(3)}
          className="rounded-lg bg-yellow-500/20 px-2 py-1 text-xs text-yellow-500"
          title="Пробний Premium на 3 дні"
        >
          🎁 +3 дні
        </button>
        <button
          onClick={() => onPremiumMonths(1)}
          className="rounded-lg bg-tg-success/20 px-2 py-1 text-xs text-tg-success"
        >
          +1м
        </button>
        <button
          onClick={() => onPremiumMonths(3)}
          className="rounded-lg bg-tg-success/20 px-2 py-1 text-xs text-tg-success"
        >
          +3м
        </button>
        <button
          onClick={() => onPremiumMonths(6)}
          className="rounded-lg bg-tg-success/20 px-2 py-1 text-xs text-tg-success"
        >
          +6м
        </button>
        {isPremium && (
          <button
            onClick={() => onPremiumMonths(0)}
            className="rounded-lg bg-tg-destructive/20 px-2 py-1 text-xs text-tg-destructive"
          >
            Зняти Premium
          </button>
        )}
      </div>
    </div>
  );
}

// Бейдж "звідки прийшов юзер" — від партнера / по рефералу / органічно
function SourceBadge({ source }: { source: AdminUser['source'] }) {
  if (source.type === 'partner') {
    return (
      <span
        className="rounded bg-tg-link/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-link"
        title={source.sublabel ? `Промокод: ${source.sublabel}` : undefined}
      >
        🤝 {source.label}
      </span>
    );
  }
  if (source.type === 'referral') {
    return (
      <span className="rounded bg-tg-success/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-success">
        👥 від {source.label}
      </span>
    );
  }
  if (source.type === 'site') {
    return (
      <span className="rounded bg-tg-link/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-link">
        🌐 сайт
      </span>
    );
  }
  if (source.type === 'seo') {
    return (
      <span className="rounded bg-yellow-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-yellow-500">
        🔍 SEO
      </span>
    );
  }
  // organic
  return (
    <span className="rounded bg-tg-hint/15 px-1.5 py-0.5 text-[10px] font-semibold text-tg-hint">
      🌱 органіка
    </span>
  );
}

// ===== Helpers =====

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return 'щойно';
  if (sec < 3600) return `${Math.floor(sec / 60)} хв тому`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} год тому`;
  if (sec < 86400 * 30) return `${Math.floor(sec / 86400)} днів тому`;
  return new Date(iso).toLocaleDateString('uk-UA');
}

function labelForStatus(s: UserStatusFilter): string {
  return {
    all: 'Усі',
    active: 'активні',
    inactive: 'сплячі',
    blocked: 'вийшли з боту',
    premium: 'Premium',
  }[s];
}

function labelForPeriod(p: UserPeriodFilter): string {
  return {
    all: 'весь час',
    today: 'зайшли сьогодні',
    week: 'зайшли за тиждень',
    month: 'зайшли за місяць',
  }[p];
}
